'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { observer } from 'mobx-react-lite';

import SectionHead from '@/components/SectionHead';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { toast } from '@/components/ui/use-toast';
import { OTPFormSchema } from '@/validations/GuestbookValidation';
import { useAuthStore } from '@/presentation/hooks/useStores';

type Props = {
  index: number;
  totalProcess: number;
  onIndexChange: (index: number) => void;
  purpose: 'LOGIN' | 'SIGNUP' | 'VERIFICATION';
};

const OTP = observer((props: Props) => {
  const authStore = useAuthStore();

  const [loading, setLoading] = useState(false);
  const form = useForm<z.infer<typeof OTPFormSchema>>({
    resolver: zodResolver(OTPFormSchema),
    defaultValues: {
      pin: '',
    },
  });

  const handleNext = () => {
    if (props.index === props.totalProcess) {
      return;
    }
    const nextIndex = props.index + 1;
    props.onIndexChange(nextIndex);
  };

  async function onSubmit(values: z.infer<typeof OTPFormSchema>) {
    try {
      setLoading(true);

      if (authStore.hasOTPSession) {
        // For signup: Validate OTP format and save it, don't register yet

        await authStore.verifyOTP(
          authStore.registrationData.phone!,
          values.pin
        );

        // Save the OTP code for later use in registration
        if (!authStore.hasErrors) {
          authStore.updateRegistrationData({
            otpCode: values.pin,
          } as any);

          toast({
            title: 'Phone number verified!',
            description: 'Please complete your profile information.',
          });

          handleNext();
        }
      } else {
        // Fallback: redirect to start the flow again
        toast({
          title: 'No active OTP session',
          description: 'Please start the registration process again.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: error.data?.message || error.message || 'Verification failed',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <SectionHead
        title="Verify your phone number"
        description={
          <p className="w-full text-center text-sm font-light md:text-base">
            Please enter the 6 digit code sent to <br />
            <b className="font-medium">
              {authStore.registrationData?.phone ||
                sessionStorage.getItem('number') ||
                'your phone number'}
              .
            </b>{' '}
            <Link
              href=".."
              className=" text-indigo-400 underline underline-offset-4"
            >
              Change number
            </Link>{' '}
          </p>
        }
      />

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="w-full space-y-6"
        >
          <FormField
            control={form.control}
            name="pin"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <InputOTP maxLength={6} {...field}>
                    <InputOTPGroup className={'space-x-2'}>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            loading={loading}
            disabled={loading}
            className=" mt-3 w-full rounded-3xl bg-motitoBlue text-white hover:bg-blue-700"
          >
            Verify Code
          </Button>
        </form>
      </Form>

      <div className="flex w-full flex-col items-center gap-4">
        <div className="flex items-center text-sm">
          <p className="text-muted">Didnt receive the code? </p>{' '}
          <Link href=".." className="text-accent">
            Resend Code{' '}
            {authStore.hasOTPSession
              ? `in ${Math.floor(authStore.otpTimeRemaining / 60)}:${String(
                  authStore.otpTimeRemaining % 60
                ).padStart(2, '0')}`
              : ''}
          </Link>
        </div>
      </div>
    </>
  );
});

export default OTP;
